/**
 * DictionaryField - Key-value pair editor
 *
 * Supports both expanded mode (list of key-value pairs) and collapsed mode (JSON text).
 * Serializes as an actual object (not a JSON string).
 * Auto-populates empty rows for easy entry.
 */

import { useState, useCallback, useMemo, useRef, useEffect, type ReactNode } from 'react';
import {
  makeStyles,
  tokens,
  mergeClasses,
  Label,
  Text,
  Button,
  Tooltip,
} from '@fluentui/react-components';
import {
  DeleteFilled,
  CodeFilled,
  TextFieldFilled,
} from '@fluentui/react-icons';
import type { ValueSegment } from '@/types/valueSegment';
import {
  generateSegmentId,
  ValueSegmentType,
  isValueSegmentArray,
  segmentsToString,
} from '@/types/valueSegment';
import { containsTemplateToken } from '@/utils/expressions';
import { ExpressionPreview } from './ExpressionPreview';
import { TokenizedInput } from './TokenizedInput';
import { useFieldPreviewFocus } from './useFieldPreviewFocus';

import {
  fieldContainerStyle,
  fieldLabelRowStyle,
  fieldLabelStyle,
  fieldRequiredStyle,
} from './fieldStyles';

const useStyles = makeStyles({
  container: fieldContainerStyle,
  labelRow: fieldLabelRowStyle,
  label: fieldLabelStyle,
  required: fieldRequiredStyle,
  modeToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  // Vertical separator between FieldActionChips (left) and mode-toggle
  // buttons (right) in the labelRow. Mirrors the same affordance in
  // ArrayField so the two editors feel consistent.
  labelRowSeparator: {
    width: '1px',
    alignSelf: 'stretch',
    marginLeft: tokens.spacingHorizontalXXS,
    marginRight: tokens.spacingHorizontalXXS,
    backgroundColor: tokens.colorNeutralStroke2,
    '@media (forced-colors: active)': {
      backgroundColor: 'CanvasText',
    },
  },
  // Wrapper for entry list + preview overlay positioning
  entryListWrapper: {
    position: 'relative',
  },
  entryList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  entryHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 32px',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXXS,
  },
  headerLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
  },
  entryRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 32px',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
  deleteButton: {
    minWidth: 'auto',
    height: '32px',
    padding: tokens.spacingHorizontalS,
  },
  deleteButtonHidden: {
    visibility: 'hidden',
  },
  error: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  duplicateWarning: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteYellowForeground1,
  },
  // Wrapper for JSON mode input + preview overlay positioning
  jsonEditorWrapper: {
    position: 'relative',
  },
  // Slot for caller-provided end actions (e.g. FieldActionChips). The
  // dictionary editor has multiple input cells, so the chips live next
  // to the mode toggle in the label row instead of being anchored to a
  // single cell.
  endActionsInLabelRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
});

interface DictionaryEntry {
  id: string;
  key: string;
  value: string;
}

export interface DictionaryFieldProps {
  name: string;
  label: string;
  value: string | ValueSegment[] | Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
  description?: string | undefined;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
  /** Label for the key column */
  keyLabel?: string | undefined;
  /** Label for the value column */
  valueLabel?: string | undefined;
  /** Action outputs for expression evaluation in preview */
  actionOutputs?: Record<string, unknown> | undefined;
  /** Whether we have run data available */
  hasRunData?: boolean | undefined;
  /** Whether the workflow has changed since last run */
  isStale?: boolean | undefined;
  /**
   * Optional slot rendered next to the mode toggle in the label row.
   * Used to host inline affordances like `FieldActionChips`. The
   * dictionary editor has multiple cells, so the chips anchor to the
   * label row rather than a single input.
   */
  endActions?: ReactNode | undefined;
}

/**
 * Parse value prop into object
 */
function parseValueToObject(value: string | ValueSegment[] | Record<string, unknown>): Record<string, unknown> {
  if (typeof value === 'object' && value !== null && !Array.isArray(value) && !isValueSegmentArray(value)) {
    return value as Record<string, unknown>;
  }

  const stringValue = isValueSegmentArray(value)
    ? segmentsToString(value)
    : typeof value === 'string'
      ? value
      : '';

  if (!stringValue || stringValue.trim() === '') {
    return {};
  }

  try {
    const parsed = JSON.parse(stringValue);
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed;
    }
    return {};
  } catch {
    return {};
  }
}

/**
 * Convert object to DictionaryEntry[] for editing
 */
function objectToEntries(obj: Record<string, unknown>): DictionaryEntry[] {
  return Object.entries(obj).map(([key, val]) => ({
    id: generateSegmentId(),
    key,
    value: typeof val === 'string' ? val : JSON.stringify(val),
  }));
}

/**
 * Convert entries to object (only non-empty entries)
 */
function entriesToObject(entries: DictionaryEntry[]): Record<string, unknown> {
  const obj: Record<string, unknown> = {};
  for (const entry of entries) {
    // Only include entries with non-empty keys
    if (entry.key.trim()) {
      const valueStr = entry.value;
      // Try to preserve types (numbers, booleans, objects)
      try {
        obj[entry.key] = JSON.parse(valueStr);
      } catch {
        obj[entry.key] = valueStr;
      }
    }
  }
  return obj;
}

/**
 * Ensure there's always at least one empty row at the end
 */
function ensureEmptyRow(entries: DictionaryEntry[]): DictionaryEntry[] {
  const lastEntry = entries[entries.length - 1];
  if (!lastEntry || lastEntry.key.trim() !== '' || lastEntry.value.trim() !== '') {
    return [
      ...entries,
      { id: generateSegmentId(), key: '', value: '' },
    ];
  }
  return entries;
}

/**
 * Check if two objects are deeply equal (for simple JSON-like objects)
 */
function objectsEqual(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export const DictionaryField = ({
  name,
  label,
  value,
  onChange,
  description,
  required,
  disabled = false,
  error,
  keyLabel = 'Key',
  valueLabel = 'Value',
  actionOutputs,
  hasRunData,
  isStale,
  endActions,
}: DictionaryFieldProps) => {
  const styles = useStyles();

  // Track expanded vs collapsed mode
  const [isExpanded, setIsExpanded] = useState(true);

  // Track focus state for showing preview (shared across both modes)
  const { isFocused, containerHandlers, previewHandlers } = useFieldPreviewFocus();

  // Parse initial value (only on mount - we use internal state for updates)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialObject = useMemo(() => parseValueToObject(value), []);

  // Internal entries state - maintains stable IDs across edits
  const [entries, setEntries] = useState<DictionaryEntry[]>(() =>
    ensureEmptyRow(objectToEntries(initialObject))
  );

  // Track if we initiated the change (to avoid re-parsing our own updates)
  const isInternalChangeRef = useRef(false);

  // Track the last object we sent out
  const lastObjectRef = useRef<Record<string, unknown>>(initialObject);

  // JSON text for collapsed mode
  const [jsonText, setJsonText] = useState(() =>
    Object.keys(initialObject).length > 0 ? JSON.stringify(initialObject, null, 2) : ''
  );
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Sync from external value changes only
  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }

    const externalObject = parseValueToObject(value);
    // Only re-parse if the external value is different from what we last sent
    if (!objectsEqual(externalObject, lastObjectRef.current)) {
      const newEntries = ensureEmptyRow(objectToEntries(externalObject));
      setEntries(newEntries);
      setJsonText(Object.keys(externalObject).length > 0 ? JSON.stringify(externalObject, null, 2) : '');
      lastObjectRef.current = externalObject;
    }
  }, [value]);

  // Computed preview of the current object
  const currentObject = useMemo(() => entriesToObject(entries), [entries]);

  // Check for duplicate keys
  const duplicateKeys = useMemo(() => {
    const keys = entries.filter(e => e.key.trim()).map(e => e.key);
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    for (const key of keys) {
      if (seen.has(key)) {
        duplicates.add(key);
      }
      seen.add(key);
    }
    return duplicates;
  }, [entries]);

  // Notify parent of changes
  const notifyChange = useCallback((newEntries: DictionaryEntry[]) => {
    const obj = entriesToObject(newEntries);
    lastObjectRef.current = obj;
    isInternalChangeRef.current = true;
    onChange(obj);
  }, [onChange]);

  // Handle entry key change
  const handleKeyChange = useCallback((entryId: string, newKey: string) => {
    setEntries((prev) => {
      const newEntries = prev.map((entry) =>
        entry.id === entryId ? { ...entry, key: newKey } : entry
      );
      const withEmptyRow = ensureEmptyRow(newEntries);
      notifyChange(withEmptyRow);
      return withEmptyRow;
    });
  }, [notifyChange]);

  // Handle entry value change
  const handleValueChange = useCallback((entryId: string, newValue: string) => {
    setEntries((prev) => {
      const newEntries = prev.map((entry) =>
        entry.id === entryId ? { ...entry, value: newValue } : entry
      );
      const withEmptyRow = ensureEmptyRow(newEntries);
      notifyChange(withEmptyRow);
      return withEmptyRow;
    });
  }, [notifyChange]);

  // Remove entry
  const handleRemoveEntry = useCallback((entryId: string) => {
    setEntries((prev) => {
      const newEntries = prev.filter((entry) => entry.id !== entryId);
      const withEmptyRow = ensureEmptyRow(newEntries);
      notifyChange(withEmptyRow);
      return withEmptyRow;
    });
  }, [notifyChange]);

  // Sync JSON text when switching to collapsed mode
  const handleSwitchToCollapsed = useCallback(() => {
    setJsonText(Object.keys(currentObject).length > 0 ? JSON.stringify(currentObject, null, 2) : '');
    setJsonError(null);
    setIsExpanded(false);
  }, [currentObject]);

  // Check if an entry is empty (for hiding delete button)
  const isEntryEmpty = (entry: DictionaryEntry) => !entry.key.trim() && !entry.value.trim();

  return (
    <div className={styles.container}>
      <div className={styles.labelRow}>
        <Label className={styles.label} htmlFor={name}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </Label>
        <div className={styles.modeToggle}>
          {endActions && (
            <>
              <span className={styles.endActionsInLabelRow}>{endActions}</span>
              <span className={styles.labelRowSeparator} aria-hidden="true" />
            </>
          )}
          <Tooltip content="Edit as key-value pairs" relationship="label">
            <Button
              appearance={isExpanded ? 'primary' : 'subtle'}
              size="small"
              icon={<TextFieldFilled />}
              onClick={() => setIsExpanded(true)}
              disabled={disabled}
              aria-label="Edit as key-value pairs"
            />
          </Tooltip>
          <Tooltip content="Edit as JSON" relationship="label">
            <Button
              appearance={!isExpanded ? 'primary' : 'subtle'}
              size="small"
              icon={<CodeFilled />}
              onClick={handleSwitchToCollapsed}
              disabled={disabled}
              aria-label="Edit as JSON"
            />
          </Tooltip>
        </div>
      </div>

      {description && (
        <Text className={styles.description}>{description}</Text>
      )}

      {isExpanded ? (
        // Expanded mode - list of key-value pairs
        <>
          <div className={styles.entryListWrapper} {...containerHandlers}>
            <div className={styles.entryList}>
              <div className={styles.entryHeader}>
                <Text className={styles.headerLabel}>{keyLabel}</Text>
                <Text className={styles.headerLabel}>{valueLabel}</Text>
                <div /> {/* Spacer for delete button */}
              </div>
              {entries.map((entry) => (
                <div key={entry.id} className={styles.entryRow}>
                  <TokenizedInput
                    name={`${name}_${entry.id}_key`}
                    value={[{
                      id: `${entry.id}_key`,
                      type: ValueSegmentType.LITERAL,
                      value: entry.key,
                    }]}
                    onChange={(segments) => handleKeyChange(entry.id, segmentsToString(segments))}
                    placeholder="Key..."
                    disabled={disabled}
                    actionOutputs={actionOutputs}
                  />
                  <TokenizedInput
                    name={`${name}_${entry.id}_value`}
                    value={[{
                      id: entry.id,
                      type: ValueSegmentType.LITERAL,
                      value: entry.value,
                    }]}
                    onChange={(segments) => handleValueChange(entry.id, segmentsToString(segments))}
                    placeholder="Value..."
                    disabled={disabled}
                    actionOutputs={actionOutputs}
                  />
                  <Tooltip content="Remove entry" relationship="label">
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<DeleteFilled />}
                      onClick={() => handleRemoveEntry(entry.id)}
                      disabled={disabled}
                      className={mergeClasses(styles.deleteButton, isEntryEmpty(entry) && styles.deleteButtonHidden)}
                      aria-label="Remove entry"
                    />
                  </Tooltip>
                </div>
              ))}
            </div>
            {duplicateKeys.size > 0 && (
              <Text className={styles.duplicateWarning}>
                Warning: Duplicate keys detected: {Array.from(duplicateKeys).join(', ')}
              </Text>
            )}
            {/* Preview - shown as overlay when focused and has value */}
            {isFocused && Object.keys(currentObject).length > 0 && (
              <ExpressionPreview
                value={currentObject}
                actionOutputs={actionOutputs}
                hasRunData={hasRunData}
                isStale={isStale}
                mode="overlay"
                {...previewHandlers}
              />
            )}
          </div>
        </>
      ) : (
        // Collapsed mode - JSON editor with expression support
        <div className={styles.jsonEditorWrapper} {...containerHandlers}>
          <TokenizedInput
            name={`${name}_json`}
            value={[{
              id: 'json',
              type: ValueSegmentType.LITERAL,
              value: jsonText,
            }]}
            onChange={(segments) => {
              const newText = segmentsToString(segments);
              setJsonText(newText);
              setJsonError(null);

              // Try to parse as JSON first so structurally valid input like
              // {"k": "${foo}"} is preserved as an object. Only fall back to
              // raw passthrough when parsing fails AND the text contains a
              // ${...} token — that's the case where the whole value is a
              // runtime expression whose shape can't be statically validated.
              try {
                const parsed = newText.trim() ? JSON.parse(newText) : {};
                if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
                  setJsonError('Value must be a JSON object');
                  return;
                }
                isInternalChangeRef.current = true;
                onChange(parsed);
                lastObjectRef.current = parsed;
              } catch {
                if (containsTemplateToken(newText)) {
                  isInternalChangeRef.current = true;
                  lastObjectRef.current = {};
                  onChange(newText as unknown as Record<string, unknown>);
                  return;
                }
                setJsonError('Invalid JSON');
              }
            }}
            placeholder="{}"
            multiline
            disabled={disabled}
            actionOutputs={actionOutputs}
          />
          {jsonError && (
            <Text className={styles.error}>{jsonError}</Text>
          )}
          {/* Preview - shown as overlay when focused and has value */}
          {isFocused && jsonText.trim() && !jsonError && (
            <ExpressionPreview
              value={currentObject}
              actionOutputs={actionOutputs}
              hasRunData={hasRunData}
              isStale={isStale}
              mode="overlay"
              {...previewHandlers}
            />
          )}
        </div>
      )}

      {error && (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      )}
    </div>
  );
};

DictionaryField.displayName = 'DictionaryField';
